// Export survey answers into a user-picked Excel template.
//
// The attribute_templates schema_definition now carries an `excelTemplateCell`
// (e.g. "D20") per question — the same mapping robotic-imaging-web uses for its
// templated form exports. Here we join those mappings to the survey's answers
// (template field id === survey leaf ref_id, falling back to cleaned title for
// legacy surveys) and stamp each answer into the picked workbook via the
// zip-level patcher, which leaves all template formatting untouched.

import type { AttributeField, TemplateField } from "./types.ts";
import type { CellWrite, SheetProtection } from "./xlsxTemplatePatcher.ts";
import { listXlsxSheetNames, readSheetColumnText, stampXlsxTemplate } from "./xlsxTemplatePatcher.ts";
import {
  collectGcEvaluationWrites,
  isNotApplicable,
  normalizeTitleKey,
  SurveyAnswers,
} from "./gcEvaluationFill.ts";
import { collectTokenWrites } from "./placeholderTokenFill.ts";
import { collectFindingsWrites } from "./summaryFindingsFill.ts";
import { collectUnitBreakdownWrites } from "./summaryUnitBreakdownFill.ts";
import type { GcsProxy, SlotAsset } from "./summaryPhotoFill.ts";
import { fillSummaryPhotos } from "./summaryPhotoFill.ts";
import { mergeReviewerEvidence } from "./reviewerEvidence.ts";

export type { CellWrite } from "./xlsxTemplatePatcher.ts";

/**
 * Filename-safe but human-readable: unlike _helpers.sanitize (which
 * underscores spaces), only strips characters filesystems reject, so the
 * "{name} - {address}.xlsx" pattern survives intact.
 */
export function safeFilename(name: string): string {
  return name.replace(/[/\\:*?"<>|]/g, "").replace(/\s+/g, " ").trim().slice(0, 150);
}

/**
 * "{name} - {address}" export filename base (no extension). Properties without
 * a name fall back to name === address upstream, so a duplicate address is
 * dropped; `fallback` covers properties with neither.
 */
export function exportFilenameBase(
  name: string | null | undefined,
  address: string | null | undefined,
  fallback: string
): string {
  const dedupedAddress = address !== name ? address : null;
  return safeFilename([name, dedupedAddress].filter(Boolean).join(" - ") || fallback);
}

/** Full US state/territory name -> 2-letter postal abbreviation. */
const US_STATE_ABBREVIATIONS: Record<string, string> = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
  colorado: "CO", connecticut: "CT", delaware: "DE", florida: "FL", georgia: "GA",
  hawaii: "HI", idaho: "ID", illinois: "IL", indiana: "IN", iowa: "IA",
  kansas: "KS", kentucky: "KY", louisiana: "LA", maine: "ME", maryland: "MD",
  massachusetts: "MA", michigan: "MI", minnesota: "MN", mississippi: "MS",
  missouri: "MO", montana: "MT", nebraska: "NE", nevada: "NV", "new hampshire": "NH",
  "new jersey": "NJ", "new mexico": "NM", "new york": "NY", "north carolina": "NC",
  "north dakota": "ND", ohio: "OH", oklahoma: "OK", oregon: "OR", pennsylvania: "PA",
  "rhode island": "RI", "south carolina": "SC", "south dakota": "SD", tennessee: "TN",
  texas: "TX", utah: "UT", vermont: "VT", virginia: "VA", washington: "WA",
  "west virginia": "WV", wisconsin: "WI", wyoming: "WY",
};

/**
 * Maps a full state name to its 2-letter postal abbreviation for filenames.
 * Already-abbreviated or unrecognized input passes through unchanged.
 */
function abbreviateState(state: string): string {
  return US_STATE_ABBREVIATIONS[state.toLowerCase()] ?? state;
}

/**
 * Deliverable filename: "City, ST, Store#, Survey Form" (no extension).
 * Falls back to the name/address base when the property has no store number;
 * city/state are simply omitted from the joined string when absent.
 */
export function deliverableFilenameBase(
  city: string | null | undefined,
  state: string | null | undefined,
  storeNumber: string | null | undefined,
  fallbackBase: string
): string {
  const store = storeNumber?.trim() || fallbackBase;
  const stateAbbr = state?.trim() ? abbreviateState(state.trim()) : null;
  const parts = [city?.trim(), stateAbbr, store, "Survey Form"].filter(Boolean);
  return safeFilename(parts.join(", "));
}

/**
 * Strips the [section] suffix from a question title and lowercases it —
 * same normalization as useTemplateOptions.cleanTitle, duplicated here so
 * this module stays free of the supabase/firebase import chain (testable).
 */
function cleanTitle(title: string): string {
  return title.replace(/\s*\[.*?\]\s*$/, "").trim().toLowerCase();
}

/** A1-style address: 1–3 column letters + row number. */
const CELL_ADDR_RE = /^[A-Z]{1,3}[1-9][0-9]*$/;

/**
 * Form-level cell mapping, mirroring robotic-imaging-web's ExcelCellMapping:
 * stamps property information or a static value into a cell, independent of
 * any survey question. Stored in attribute_templates.excel_cell_mappings.
 */
export interface ExcelCellMapping {
  id?: string;
  cell: string;
  sheet?: string | null;
  source:
    | { kind: "projectProperty"; property: string }
    | { kind: "projectLink"; link: string }
    | { kind: "static"; value?: string | number };
}

/** The properties-table columns mapping resolution can draw from. */
export interface ExportPropertyInfo {
  id: string;
  name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  country: string | null;
  store_number: string | null;
  square_footage: string | null;
  scan_date: string | null;
  climate_zone: string | null;
  hvac_tonnage: string | null;
  property_type: string | null;
  description: string | null;
  ref_id: string | null;
}

export const EXPORT_PROPERTY_COLUMNS =
  "id, name, address, city, state, zip_code, country, store_number, square_footage, scan_date, climate_zone, hvac_tonnage, property_type, description, ref_id";

export interface MappingContext {
  property?: ExportPropertyInfo | null;
  /** The exported survey's captured_at, for date mappings. */
  capturedAt?: string | null;
}

/** Web-style camelCase mapping keys → properties-table columns. */
const PROPERTY_KEY_TO_COLUMN: Record<string, keyof ExportPropertyInfo> = {
  name: "name",
  address: "address",
  city: "city",
  state: "state",
  zipCode: "zip_code",
  country: "country",
  storeNumber: "store_number",
  squareFootage: "square_footage",
  scanDate: "scan_date",
  climateZone: "climate_zone",
  hvacTonnage: "hvac_tonnage",
  propertyType: "property_type",
  description: "description",
  refId: "ref_id",
};

/** "2026-06-24" / ISO timestamps → "6/24/2026"; anything else passes through. */
function formatDateValue(value: string | null | undefined): string | null {
  if (!value) return null;
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return value;
  return `${Number(m[2])}/${Number(m[3])}/${m[1]}`;
}

function resolveMappingValue(
  mapping: ExcelCellMapping,
  ctx: MappingContext
): string | number | null {
  const source = mapping.source;
  switch (source?.kind) {
    case "static":
      return source.value ?? null;
    case "projectProperty": {
      if (source.property === "capturedAt" || source.property === "surveyDate") {
        return formatDateValue(ctx.capturedAt);
      }
      if (source.property === "scanDate") {
        return formatDateValue(ctx.property?.scan_date);
      }
      // Composite location keys for header cells like "[City, State ZIP]".
      if (source.property === "cityState" || source.property === "cityStateZip") {
        const cityState = [ctx.property?.city, ctx.property?.state].filter(Boolean).join(", ");
        if (!cityState) return null;
        const zip = source.property === "cityStateZip" ? ctx.property?.zip_code : null;
        return zip ? `${cityState} ${zip}` : cityState;
      }
      const column = PROPERTY_KEY_TO_COLUMN[source.property];
      const raw = column ? ctx.property?.[column] : null;
      if (raw === null || raw === undefined || raw === "") return null;
      return coerceNumeric(String(raw));
    }
    default:
      // projectLink kinds (matterport, photo galleries…) are web-app concepts
      // with no analog here.
      return null;
  }
}

/** Cell writes from the form-level mappings; unresolvable mappings are skipped. */
export function collectMappingCellWrites(
  mappings: ExcelCellMapping[] | undefined,
  ctx: MappingContext
): CellWrite[] {
  const writes: CellWrite[] = [];
  for (const mapping of mappings ?? []) {
    const cell = mapping.cell?.trim().toUpperCase();
    if (!cell || !CELL_ADDR_RE.test(cell)) continue;
    const value = resolveMappingValue(mapping, ctx);
    if (value === null || value === "") continue;
    writes.push({ sheet: mapping.sheet ?? null, cell, value });
  }
  return writes;
}

// Survey answers carry units as text ("8625 SF", "3 Tons", "26 Years") but the
// templates expect plain numbers in those cells. A number (with optional
// thousands separators) followed by at most a known unit word becomes a
// numeric cell; anything else stays text.
const NUMBER_WITH_UNIT_RE =
  /^(-?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?)\s*(sf|sq\.?\s*ft\.?|sqft|sqm|tons?|years?|yrs?|%)?$/i;

function coerceNumeric(text: string): string | number {
  const match = text.match(NUMBER_WITH_UNIT_RE);
  if (!match) return text;
  const digits = match[1].replace(/,/g, "");
  // A leading zero means an identifier (serial number), not a quantity.
  if (/^-?0\d/.test(digits)) return text;
  const n = Number(digits);
  return Number.isFinite(n) ? n : text;
}

function formatAnswer(value: unknown): string | number | null {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) {
    const joined = value.map(String).filter(Boolean).join(", ");
    return joined || null;
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value;
  const text = String(value).trim();
  return text ? coerceNumeric(text) : null;
}

/** ref_id/id → answer, plus cleaned-title fallback keys for legacy surveys. */
export function buildAnswerMap(fields: AttributeField[]): Map<string, unknown> {
  const map = new Map<string, unknown>();
  // DG's catalog has real duplicate titles across sections (Interior - Office &
  // Breakroom and Restrooms both ask "Number of broken, damaged, missing, or
  // stained ceiling tiles" — different questions, different answers). A
  // first-wins title key would silently hand one section's answer to the
  // other's cell; drop the key on a second sighting instead, same as
  // fillAnswerGapsFromProdData's ambiguous-title guard.
  const ambiguousTitleKeys = new Set<string>();

  const walk = (list: AttributeField[]) => {
    for (const field of list) {
      if (field.type === "node") {
        walk(field.fields);
        continue;
      }
      const value = (field as { value?: unknown }).value;
      if (field.id) map.set(field.id, value);
      const refId = (field as { ref_id?: string }).ref_id;
      if (refId) map.set(refId, value);
      if (field.title) {
        const key = `title:${cleanTitle(field.title)}`;
        if (ambiguousTitleKeys.has(key)) continue;
        if (map.has(key)) {
          map.delete(key);
          ambiguousTitleKeys.add(key);
        } else {
          map.set(key, value);
        }
      }
    }
  };
  walk(fields);
  return map;
}

/**
 * Walks the template tree and pairs each `excelTemplateCell` mapping with the
 * survey's answer for that question. Unanswered or unmapped questions are
 * skipped. Returns the writes plus how many questions carried a mapping at
 * all, so callers can tell "no mappings in template" from "nothing answered".
 */
export function collectTemplateCellWrites(
  templateFields: TemplateField[],
  answers: Map<string, unknown>
): { writes: CellWrite[]; mapped: number } {
  const writes: CellWrite[] = [];
  let mapped = 0;

  const walk = (fields: TemplateField[]) => {
    for (const field of fields) {
      if (field.type === "node" && field.fields) {
        walk(field.fields);
        continue;
      }

      // Per-option mode (robotic-imaging-web "Map options to cells"): each
      // selected option — or a switch state, arriving as "true"/"false" —
      // writes its configured value into its own cell and replaces the
      // single-cell mapping below, mirroring the web app's export.
      const optionCells = field.excelUsePerOption
        ? (field.excelOptionCells ?? []).filter((entry) => entry.cell)
        : [];
      if (optionCells.length > 0) {
        mapped++;
        const raw = answers.has(field.id)
          ? answers.get(field.id)
          : answers.get(`title:${cleanTitle(field.title)}`);
        const selected = Array.isArray(raw)
          ? raw.map(String)
          : raw === null || raw === undefined || raw === ""
            ? []
            : [String(raw)];
        for (const entry of optionCells) {
          if (!selected.includes(entry.option)) continue;
          const cell = entry.cell.trim().toUpperCase();
          if (!CELL_ADDR_RE.test(cell)) {
            console.warn(`[excel export] Invalid cell address "${entry.cell}" on "${field.title}"; skipping.`);
            continue;
          }
          const value = formatAnswer(entry.value || entry.option);
          if (value === null) continue;
          writes.push({ sheet: entry.sheet ?? null, cell, value });
        }
        continue;
      }

      // One answer can be stamped into several cells (a Survey Form response
      // cell plus summary-sheet headers): the single excelTemplateCell plus
      // any excelTemplateCells entries.
      const targets: Array<{ cell?: string | null; sheet?: string | null }> = [
        { cell: field.excelTemplateCell, sheet: field.excelTemplateSheet },
        ...(field.excelTemplateCells ?? []),
      ].filter((t) => t.cell);
      if (targets.length === 0) continue;
      mapped++;

      const raw = answers.has(field.id)
        ? answers.get(field.id)
        : answers.get(`title:${cleanTitle(field.title)}`);
      const value = formatAnswer(raw);
      if (value === null) continue;

      for (const target of targets) {
        const cell = target.cell!.trim().toUpperCase();
        if (!CELL_ADDR_RE.test(cell)) {
          console.warn(`[excel export] Invalid cell address "${target.cell}" on "${field.title}"; skipping.`);
          continue;
        }
        writes.push({ sheet: target.sheet ?? null, cell, value });
      }
    }
  };

  walk(templateFields);
  return { writes, mapped };
}

/** Sheet names of the picked .xlsx, so the UI can offer a target-sheet choice. */
export async function listTemplateSheets(file: File): Promise<string[]> {
  return listXlsxSheetNames(await file.arrayBuffer());
}

/**
 * Adds prod_data answers directly into the answer map by question title.
 * fillAnswerGapsFromProdData covers leaves the tree HAS — but questions added
 * only to prod_data (e.g. a manually-added HVAC 4 section on a survey whose
 * source flag is "data") have no tree leaf at all and would be invisible to
 * the export. Existing non-empty answers are never displaced.
 */
export function mergeProdDataAnswers(
  answerMap: Map<string, unknown>,
  prodData: Record<string, unknown> | null | undefined
): Map<string, unknown> {
  if (!prodData || typeof prodData !== "object") return answerMap;
  const merged = new Map(answerMap);
  for (const raw of Object.values(prodData)) {
    const entry = raw as {
      question_title?: string | null;
      final_survey_answer?: unknown;
      human_answer?: unknown;
      ai_answer?: unknown;
    };
    if (!entry || typeof entry !== "object" || !entry.question_title) continue;
    const answer = [entry.final_survey_answer, entry.human_answer, entry.ai_answer].find(
      (v) =>
        v !== null &&
        v !== undefined &&
        v !== "" &&
        !isNotApplicable(v) &&
        !(Array.isArray(v) && v.length === 0)
    );
    if (answer === undefined) continue;
    const key = `title:${cleanTitle(String(entry.question_title))}`;
    const existing = merged.get(key);
    if (existing === null || existing === undefined || existing === "" || isNotApplicable(existing)) {
      merged.set(key, answer);
    }
  }
  return merged;
}

/** Asset fields the unit-answer merge can draw from (see mergeAssetUnitAnswers). */
export interface UnitSourceAsset {
  name: string;
  category: string;
  modelNumber?: string | null;
  serialNumber?: string | null;
  ageYears?: number | null;
  manufactureDate?: string | null;
  attributes?: Record<string, unknown> | null;
}

/** Serials compare ignoring whitespace/case — the same plate, typed twice. */
export const normSerial = (s: unknown) => String(s ?? "").replace(/\s+/g, "").toUpperCase();

/**
 * Which unit an asset row is, deterministically: its serial matching a unit's
 * surveyed serial (the same nameplate), else a unit number in its own name.
 * Never its position in the list — an unattributable row stays unattributed.
 */
export function assetUnit(
  asset: { name: string; serialNumber?: string | null },
  serialToUnit: Map<string, number>,
  unitNumberFromName: (name: string) => number | null
): number | null {
  const serial = normSerial(asset.serialNumber);
  const bySerial = serial ? serialToUnit.get(serial) ?? null : null;
  return bySerial ?? unitNumberFromName(asset.name);
}

function assetTonnage(asset: UnitSourceAsset): number | null {
  for (const [key, value] of Object.entries(asset.attributes ?? {})) {
    if (!/ton/i.test(key)) continue;
    const n = Number(String(value).match(/\d+(?:\.\d+)?/)?.[0]);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const fromName = asset.name.match(/(\d+(?:\.\d+)?)\s*t(?:ons?)?\b/i);
  return fromName ? Number(fromName[1]) : null;
}

/**
 * Fills unit answers from the property's HVAC asset rows when the survey has
 * none — some sites capture units 4–6 as assets (nameplate data: model,
 * serial, age, tonnage) without survey questions for them. Only empty/absent
 * answer keys are filled, so surveyed units always win. Unit numbers come
 * from the asset name, else list position. Action derives from the
 * template's own rule (≥10 yrs = Replace, else Tune-Up) when age is known.
 */
export function mergeAssetUnitAnswers(
  answerMap: Map<string, unknown>,
  assets: UnitSourceAsset[],
  nowYear: number = new Date().getFullYear()
): Map<string, unknown> {
  // Unit number only from a number ADJACENT to a unit-ish word in the asset's
  // OWN name — "5 Ton Rheem RTU" must not read as unit 5, and list position
  // must never imply one: an asset that doesn't name its unit can't be
  // attributed without guessing, and a guess fabricates a unit (and a
  // $/ton cost) in a client deliverable. Name the asset "HVAC 4" to map it.
  const unitNumber = (name: string): number | null => {
    const n = Number(name.match(/(?:hvac|rtu|unit)[\s#._-]*(\d+)/i)?.[1]);
    return Number.isFinite(n) && n >= 1 && n <= 12 ? n : null;
  };

  const hvac = assets
    .filter((a) => /hvac|rtu/i.test(a.category) || /hvac|rtu/i.test(a.name))
    .sort((a, b) => {
      const na = unitNumber(a.name) ?? Infinity;
      const nb = unitNumber(b.name) ?? Infinity;
      return na - nb || a.name.localeCompare(b.name);
    });
  if (hvac.length === 0) return answerMap;

  const merged = new Map(answerMap);
  const setIfEmpty = (title: string, value: unknown) => {
    if (value === null || value === undefined || value === "" || isNotApplicable(value)) return;
    const key = `title:${cleanTitle(title)}`;
    const existing = merged.get(key);
    // An "N/A" survey answer reads as unanswered — asset data may fill it.
    if (existing === null || existing === undefined || existing === "" || isNotApplicable(existing)) {
      merged.set(key, value);
    }
  };

  // Serial is the exact join: the survey and the asset row were read off the
  // same nameplate. Falls back to a unit number in the asset's own name.
  const answers = new SurveyAnswers(answerMap);
  const serialToUnit = new Map<string, number>();
  for (let n = 1; n <= 12; n++) {
    const s = normSerial(answers.text(`Unit ${n} — Serial Number`));
    if (s && !serialToUnit.has(s)) serialToUnit.set(s, n);
  }

  const claimed = new Set<number>();
  for (const asset of hvac) {
    const unit = assetUnit(asset, serialToUnit, unitNumber);
    if (unit === null) continue; // unattributable — never guess by position
    if (claimed.has(unit)) continue; // duplicate row for a unit already taken
    claimed.add(unit);

    let age = asset.ageYears ?? null;
    if (age === null && asset.manufactureDate) {
      const year = Number(asset.manufactureDate.match(/(?:19|20)\d{2}/)?.[0]);
      if (year) age = Math.max(0, nowYear - year);
    }

    setIfEmpty(`Unit ${unit} — Tonnage`, assetTonnage(asset));
    setIfEmpty(`Unit ${unit} — Age (years)`, age);
    setIfEmpty(`Unit ${unit} — Action`, age !== null ? (age >= 10 ? "Replace" : "Tune-Up") : null);
    setIfEmpty(`Unit ${unit} — Model Number`, asset.modelNumber);
    setIfEmpty(`Unit ${unit} — Serial Number`, asset.serialNumber);
  }
  return merged;
}

/** The template's printed per-unit rule: a unit this old is replaced, not tuned up. */
export const HVAC_REPLACE_AGE_YEARS = 10;

/**
 * A survey leaf is keyed in the answer map by its id AND ref_id AND title (see
 * buildAnswerMap), and collectTemplateCellWrites resolves by id first — so a
 * title-only rewrite never reaches the Survey Form cell. Index every question's
 * id/ref_id keys by normalized title so a correction can reach all of them.
 */
function indexIdKeysByTitle(fields?: AttributeField[]): Map<string, string[]> {
  const idKeysByTitle = new Map<string, string[]>();
  const walk = (list: AttributeField[]) => {
    for (const field of list) {
      if (field.type === "node") {
        walk(field.fields);
        continue;
      }
      const key = normalizeTitleKey(field.title ?? "");
      if (!key) continue;
      const ids = idKeysByTitle.get(key) ?? [];
      const f = field as { id?: string; ref_id?: string };
      if (f.id) ids.push(f.id);
      if (f.ref_id) ids.push(f.ref_id);
      if (ids.length) idKeysByTitle.set(key, ids);
    }
  };
  if (fields) walk(fields);
  return idKeysByTitle;
}

/**
 * Synthetic answer title recording that a unit's surveyed age was dropped as
 * unusable. Like actionOverrideNoteTitle, no workbook label matches it, so it
 * is never stamped into a cell — the GC basis note reads it back.
 */
export const ageGapNoteTitle = (unit: number) => `Unit ${unit} — Age Gap Note`;

/** The GC basis disclosure for a unit whose recorded age was not usable. */
export const AGE_GAP_NOTE = "age MISSING per survey (≥10 yrs rule not applied)";

/**
 * Drops a surveyed unit age of 0 (or negative) — it is not an age.
 *
 * Reviewers type 0 into the age box when a nameplate is unreadable instead of
 * using "Can't answer" (36 such entries across 22 DG stores on 2026-09-01).
 * Left alone, that 0 reached the client deliverable twice over: printed as
 * "0 yrs old" in the GC basis and the breakdown's Yrs column, and — sitting
 * under the template's 10-yr threshold — holding the line at Tune-Up
 * ($350/ton) where a real age would have forced Replace ($3,500/ton).
 *
 * Runs BEFORE mergeAssetUnitAnswers deliberately: clearing the bogus answer
 * lets a nameplate age from the property's asset rows fill the gap, and keeps
 * this rule off asset-derived ages — a unit built this year genuinely IS 0
 * years old, and only a *surveyed* 0 is the reviewer's shorthand for "unknown".
 * The keys are deleted rather than blanked because SurveyAnswers resolves a
 * normalized title first-wins, and a surviving "" would mask the asset fill.
 */
export function dropUnusableUnitAges(
  answerMap: Map<string, unknown>,
  fields?: AttributeField[]
): Map<string, unknown> {
  const answers = new SurveyAnswers(answerMap);
  const merged = new Map(answerMap);
  const idKeysByTitle = indexIdKeysByTitle(fields);

  for (let unit = 1; unit <= 12; unit++) {
    const age = answers.num(`Unit ${unit} — Age (years)`) ?? answers.num(`Unit ${unit} — Age`);
    if (age === null || age > 0) continue;

    const target = normalizeTitleKey(`Unit ${unit} — Age`);
    for (const key of [...merged.keys()]) {
      if (key.startsWith("title:") && normalizeTitleKey(key.slice("title:".length)) === target) {
        merged.delete(key);
      }
    }
    for (const idKey of idKeysByTitle.get(target) ?? []) merged.delete(idKey);
    merged.set(`title:${cleanTitle(ageGapNoteTitle(unit))}`, AGE_GAP_NOTE);
  }
  return merged;
}

/**
 * Synthetic answer title carrying the audit trail for an age-rule override.
 * No workbook label matches it, so it is never stamped into a cell — the GC
 * basis note reads it back through the normal SurveyAnswers lookup.
 */
export const actionOverrideNoteTitle = (unit: number) => `Unit ${unit} — Action Override Note`;

/**
 * Enforces the template's own per-unit rule ("≥10 yrs = Replace", Formula
 * Reference Guide B15): a unit aged 10+ whose recorded action is Tune-Up
 * (or blank/N/A) is corrected to Replace, so the Survey Form cell, the GC
 * cost formula ($3,500/ton vs $350/ton) and the breakdown table all agree.
 *
 * One-directional by design: a young unit recorded as Replace is left alone —
 * a surveyor may have found it failed, and the rule only says age FORCES
 * replacement, never that youth forbids it. The original answer is preserved
 * in the override note (see actionOverrideNoteTitle) because the correction
 * moves real money.
 */
export function applyHvacAgeActionRule(
  answerMap: Map<string, unknown>,
  fields?: AttributeField[],
  thresholdYears: number = HVAC_REPLACE_AGE_YEARS
): Map<string, unknown> {
  const answers = new SurveyAnswers(answerMap);
  const merged = new Map(answerMap);

  const idKeysByTitle = indexIdKeysByTitle(fields);

  for (let unit = 1; unit <= 12; unit++) {
    const age = answers.num(`Unit ${unit} — Age (years)`) ?? answers.num(`Unit ${unit} — Age`);
    if (age === null || age < thresholdYears) continue;
    const action = answers.text(`Unit ${unit} — Action`);
    if (action && /^replace/i.test(action)) continue; // already correct

    // Overwrite in place under every key the answer lives under — the title
    // dialect ("HVAC 4 - Action") AND the id/ref_id keys the id-join reads.
    const target = normalizeTitleKey(`Unit ${unit} — Action`);
    let found = false;
    for (const key of merged.keys()) {
      if (key.startsWith("title:") && normalizeTitleKey(key.slice("title:".length)) === target) {
        merged.set(key, "Replace");
        found = true;
      }
    }
    for (const idKey of idKeysByTitle.get(target) ?? []) {
      if (merged.has(idKey)) merged.set(idKey, "Replace");
    }
    if (!found) merged.set(`title:${cleanTitle(`Unit ${unit} — Action`)}`, "Replace");

    merged.set(
      `title:${cleanTitle(actionOverrideNoteTitle(unit))}`,
      action ? `surveyor recorded "${action}"` : "no action was recorded"
    );
  }
  return merged;
}

export const SURVEY_FORM_LABEL_SHEET = "Survey Form";

/**
 * The sheet that writes without an explicit worksheet resolve to. Priority:
 * the explicit default (attribute_templates.excel_default_sheet, or the
 * picker's choice) → a tab named like "Survey Form" → null, which lets the
 * stamper fall back to the workbook's first tab. Resolving this once keeps the
 * stamper and the label/GC fills (which used to assume "Survey Form" while the
 * stamper assumed first-tab) on the same sheet.
 */
export async function resolveDefaultSheet(
  templateBuf: ArrayBuffer,
  explicit?: string | null
): Promise<string | null> {
  if (explicit) return explicit;
  const sheets = await listXlsxSheetNames(templateBuf);
  return sheets.find((name) => /survey.?form/i.test(name)) ?? null;
}

/**
 * Anchor for the DG summary tab — its title has shipped as "DG Leasing -
 * Summary", "… — Summary", and "DG Leasing ONLY - Summary", but always pairs
 * "Leasing" with a trailing "Summary". Resolved per workbook instead of matching
 * a fixed string, which broke silently on every rename.
 */
export const SUMMARY_SHEET_RE = /leasing.*summary/i;

/**
 * The Survey Form sheet is locked on export so recipients can't alter the
 * captured survey responses. Password gates Excel's Unprotect Sheet so it isn't
 * a one-click undo; RI staff use it to make corrections. (Sheet protection is a
 * tamper deterrent, not encryption — the password only needs to stop casual edits.)
 */
export const SURVEY_FORM_LOCK: SheetProtection = {
  sheets: [SURVEY_FORM_LABEL_SHEET],
  password: "RoboticImaging",
};

/** The Survey Form's question labels (column C), row → label. */
export async function readSurveyFormLabels(templateBuf: ArrayBuffer): Promise<Map<number, string>> {
  return readSheetColumnText(templateBuf, SURVEY_FORM_LABEL_SHEET, "C");
}

/**
 * Adds title-keyed entries derived from the Survey Form's own question labels:
 * each stamped answer cell sits next to its label (column C), so pairing them
 * resolves answers by the TEMPLATE's wording even when the survey template's
 * question titles drifted from it. Existing keys are never overwritten.
 */
export function augmentAnswersWithFormLabels(
  answerMap: Map<string, unknown>,
  formLabels: Map<number, string>,
  surveyWrites: CellWrite[],
  defaultSheet?: string | null
): Map<string, unknown> {
  if (formLabels.size === 0) return answerMap;
  const augmented = new Map(answerMap);
  for (const write of surveyWrites) {
    if (write.value === null) continue;
    const sheet = (write.sheet ?? defaultSheet ?? SURVEY_FORM_LABEL_SHEET).trim().toLowerCase();
    if (sheet !== "survey form") continue;
    const row = Number(write.cell.match(/\d+/)?.[0] ?? NaN);
    const label = formLabels.get(row);
    if (!label) continue;
    const key = `title:${cleanTitle(label)}`;
    if (!augmented.has(key)) augmented.set(key, write.value);
  }
  return augmented;
}

/**
 * Fallback mapping for questions absent from the survey template's
 * excelTemplateCell mappings (e.g. HVAC units 4–6): the workbook's Survey
 * Form rows ARE the mapping. Each label row whose answer cell isn't already
 * targeted gets the survey's answer for that label (exact normalized title
 * match — SurveyAnswers' dash/case/whitespace-insensitive lookup). Labels
 * appearing more than once in the sheet match nothing (never guess), and
 * template-mapped writes always win.
 */
export function collectLabelMatchedWrites(
  formLabels: Map<number, string>,
  answers: { raw(title: string): unknown },
  existingWrites: CellWrite[],
  defaultSheet?: string | null
): CellWrite[] {
  if (formLabels.size === 0) return [];

  // Answer column = where the template-mapped Survey Form writes land
  // (column D in the DG template); majority vote keeps it template-agnostic.
  const columnVotes = new Map<string, number>();
  const occupied = new Set<string>();
  for (const write of existingWrites) {
    const sheet = (write.sheet ?? defaultSheet ?? SURVEY_FORM_LABEL_SHEET).trim().toLowerCase();
    if (sheet !== "survey form") continue;
    occupied.add(write.cell.toUpperCase());
    const col = write.cell.match(/^[A-Z]+/i)?.[0]?.toUpperCase();
    if (col) columnVotes.set(col, (columnVotes.get(col) ?? 0) + 1);
  }
  const answerColumn =
    [...columnVotes.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "D";

  // Ambiguous labels (repeated per section) must match nothing.
  const labelCounts = new Map<string, number>();
  for (const label of formLabels.values()) {
    const key = cleanTitle(label);
    labelCounts.set(key, (labelCounts.get(key) ?? 0) + 1);
  }

  const writes: CellWrite[] = [];
  for (const [row, label] of formLabels) {
    if ((labelCounts.get(cleanTitle(label)) ?? 0) > 1) continue;
    const cell = `${answerColumn}${row}`;
    if (occupied.has(cell)) continue;
    const value = formatAnswer(answers.raw(label));
    if (value === null) continue;
    writes.push({ sheet: SURVEY_FORM_LABEL_SHEET, cell, value });
  }
  return writes;
}

/** Everything generateSurveyExcel needs; fetching/delivery stay with callers. */
export interface GenerateSurveyExcelParams {
  /** Template workbook bytes + original filename (extension decides xlsm/xlsx). */
  template: { buf: ArrayBuffer; name: string };
  templateFields: TemplateField[];
  fields: AttributeField[];
  propertyName?: string | null;
  propertyAddress?: string | null;
  /** Form-level property/static mappings, stamped alongside the answers. */
  excelCellMappings?: ExcelCellMapping[];
  mappingContext?: MappingContext;
  /** Property assets (full-shot/nameplate photo pairs) for the Summary photo slots. */
  assets?: Array<SlotAsset & UnitSourceAsset>;
  /** The survey row's prod_data — answers for questions absent from the tree. */
  prodData?: Record<string, unknown> | null;
  /**
   * Live reviewer evidence by question id, merged into prod_data for the photo
   * fill. Injected: the browser fetches it under the caller's RLS identity,
   * the edge function with the service role.
   */
  reviewerEvidence?: Map<string, unknown[]>;
  /** gs:// → fetchable-URL resolver (qa-video proxy); injected per environment. */
  gcsProxy?: GcsProxy;
  /**
   * Sheet for writes without a per-question excelTemplateSheet. When absent,
   * resolved via resolveDefaultSheet (a "Survey Form"-named tab, else the
   * workbook's first sheet).
   */
  defaultSheet?: string | null;
  /** Stage updates for progress UI. */
  onProgress?: (message: string) => void;
}

/**
 * Stamps the survey's answers into the template workbook and returns the
 * finished file. Environment-agnostic: no fetching, no download side effects —
 * runs identically in the browser and the generate-survey-export edge function.
 */
export async function generateSurveyExcel(
  params: GenerateSurveyExcelParams
): Promise<{ written: number; mapped: number; data?: Blob; filename?: string }> {
  const { template, templateFields, fields, propertyName, propertyAddress, defaultSheet } = params;

  const progress = params.onProgress ?? (() => {});
  // Answer precedence: tree answers → prod_data (incl. questions the tree
  // doesn't have) → asset nameplate data for units the survey lacks; then the
  // template's ≥10 yrs = Replace rule corrects the assembled answers. A
  // surveyed unit age of 0 is dropped before the asset merge, so a real
  // nameplate age can take its place (see dropUnusableUnitAges).
  const answerMap = applyHvacAgeActionRule(
    mergeAssetUnitAnswers(
      dropUnusableUnitAges(mergeProdDataAnswers(buildAnswerMap(fields), params.prodData), fields),
      params.assets ?? []
    ),
    fields
  );
  const answerWrites = collectTemplateCellWrites(templateFields, answerMap);
  const mappingWrites = collectMappingCellWrites(params.excelCellMappings, params.mappingContext ?? {});
  const mapped = answerWrites.mapped + (params.excelCellMappings?.length ?? 0);
  if (mapped === 0) return { written: 0, mapped: 0 };

  progress("Reading template…");
  const buf = template.buf;
  const resolvedDefaultSheet = await resolveDefaultSheet(buf, defaultSheet);
  const formLabels = await readSurveyFormLabels(buf);
  // Fallback mapping via the workbook's own question labels: answered
  // questions the template doesn't map (HVAC units 4–6) still land in their
  // Survey Form rows, feeding the GC formulas and Summary fills downstream.
  const labelWrites = collectLabelMatchedWrites(
    formLabels,
    new SurveyAnswers(answerMap),
    answerWrites.writes,
    resolvedDefaultSheet
  );
  // N/A now always stamps: the reviewer's answer_status is the gate (a
  // closed-enum reason behind it), so the template's own format column no
  // longer needs to second-guess whether N/A belongs in a cell.
  const surveyWrites = [...answerWrites.writes, ...labelWrites];
  // Survey Form labels make answers resolvable by the template's own wording.
  const augmentedMap = augmentAnswersWithFormLabels(answerMap, formLabels, surveyWrites, resolvedDefaultSheet);
  const gc = await collectGcEvaluationWrites(buf, augmentedMap, surveyWrites, resolvedDefaultSheet);
  // Placeholder tokens first so explicit cell mappings win on conflicts.
  const surveyAnswers = new SurveyAnswers(augmentedMap);
  const tokenWrites = await collectTokenWrites(buf, {
    property: params.mappingContext?.property,
    capturedAt: params.mappingContext?.capturedAt,
    answers: surveyAnswers,
  });
  const findingsWrites = await collectFindingsWrites(buf, surveyAnswers);
  const breakdownWrites = await collectUnitBreakdownWrites(buf, surveyAnswers);
  progress("Filling template…");
  const { data, written } = await stampXlsxTemplate(
    buf,
    [...tokenWrites, ...findingsWrites, ...breakdownWrites, ...surveyWrites, ...mappingWrites, ...gc.writes],
    resolvedDefaultSheet,
    gc.restyles,
    SURVEY_FORM_LOCK
  );
  // Reviewer-pasted photos live in qa_items_evidence; merge them into prod_data
  // so the photo collector (which reads prod_data) sees evidence added after a
  // decision was saved — not just what got flushed into the column.
  const photoProdData = params.reviewerEvidence
    ? mergeReviewerEvidence(params.prodData, params.reviewerEvidence)
    : params.prodData;
  const { data: withPhotos } = await fillSummaryPhotos(
    data,
    fields,
    params.assets ?? [],
    surveyAnswers,
    progress,
    photoProdData,
    params.gcsProxy
  );

  const base = deliverableFilenameBase(
    params.mappingContext?.property?.city,
    params.mappingContext?.property?.state,
    params.mappingContext?.property?.store_number,
    exportFilenameBase(propertyName, propertyAddress, template.name.replace(/\.[^.]+$/, ""))
  );
  // Keep the template's extension: .xlsm templates carry macros (e.g. the
  // Summary's print-to-PDF button) that survive the zip-level stamping.
  const ext = template.name.match(/\.xlsm$/i) ? "xlsm" : "xlsx";
  const filename = `${base}.${ext}`;
  return { written, mapped, data: withPhotos, filename };
}
